<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title">Offer Listings</h4>
          <p class="card-category">Search and manage job offers</p>
        </div>
        <div class="card-body">
          <!-- Search and Filters -->
          <div class="row mb-3">
            <div class="col-md-6">
              <BFormInput
                v-model="filters.search"
                placeholder="Search by ID, title, company name, or ref #..."
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-6">
              <div class="d-flex justify-content-end align-items-center h-100" style="gap: 20px;">
                <BFormCheckbox v-model="filters.hide_agency" switch @input="applyFilters">
                  Hide Agency
                </BFormCheckbox>
                <BFormCheckbox v-model="filters.hide_feed" switch @input="applyFilters">
                  Hide Feed
                </BFormCheckbox>
                <BFormCheckbox v-model="filters.hide_jobbank" switch @input="applyFilters">
                  Hide JobBank
                </BFormCheckbox>
              </div>
            </div>
          </div>

          <!-- Results info -->
          <div class="mb-2">
            <small class="text-muted">
              <span v-if="!isTableBusy">{{ total.toLocaleString() }} offers found</span>
              <span v-else>Loading...</span>
            </small>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <BTable
              :items="offers"
              :fields="fields"
              :busy="isTableBusy"
              hover
              show-empty
              empty-text="No offers found"
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <BSpinner class="align-middle"></BSpinner>
                  <span class="ml-2">Loading...</span>
                </div>
              </template>
              <template #cell(id)="{ item }">
                <code>{{ item.id }}</code>
              </template>
              <template #cell(title)="{ item }">
                <router-link
                  :to="{
                    name: 'offer',
                    params: { offerId: item.id }
                  }"
                  class="font-weight-medium"
                >
                  {{ item.content[1] ? item.content[1].title : item.content[0].title }}
                </router-link>
                <div class="mt-1">
                  <BBadge v-if="item.feed_in" variant="info" class="mr-1" :title="'Feed: ' + item.feed_in.legacy_client">
                    {{ item.feed_in.legacy_client }}
                  </BBadge>
                  <BBadge v-if="item.is_agency" variant="warning">Agency</BBadge>
                </div>
              </template>
              <template #cell(company)="{ item }">
                <router-link
                  v-if="item.offer_company"
                  :to="{
                    name: 'company',
                    params: { companyId: item.offer_company.id }
                  }"
                >
                  {{ item.offer_company.name_fr }}
                </router-link>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(reference_number)="{ item }">
                <code v-if="item.internal_reference_number">{{ item.internal_reference_number }}</code>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(noc)="{ item }">
                <span v-if="item.noc_job_title" class="small">
                  {{ item.noc_number }} - {{ item.noc_job_title.title_fr }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(created_at)="{ item }">
                <span class="small">{{ formatDate(item.creation_date) }}</span>
              </template>
            </BTable>

            <BPagination
              v-if="total > perPage"
              :disabled="isTableBusy"
              v-model="currentPage"
              @input="fetchOffers"
              :total-rows="total"
              :per-page="perPage"
              align="center"
            ></BPagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BTable,
  BSpinner,
  BPagination,
  BFormInput,
  BFormCheckbox,
  BBadge
} from "bootstrap-vue";
import debounce from "lodash/debounce";

export default {
  components: {
    BTable,
    BSpinner,
    BPagination,
    BFormInput,
    BFormCheckbox,
    BBadge
  },
  data() {
    return {
      filters: {
        search: "",
        hide_agency: false,
        hide_feed: false,
        hide_jobbank: false
      },
      offers: [],
      currentPage: 1,
      perPage: 15,
      total: 0,
      isTableBusy: true,
      fields: [
        { key: "id", label: "ID", thStyle: { width: "80px" } },
        { key: "title", label: "Title" },
        { key: "company", label: "Company" },
        { key: "reference_number", label: "Ref #", thStyle: { width: "120px" } },
        { key: "noc", label: "NOC", thStyle: { width: "200px" } },
        { key: "created_at", label: "Date", thStyle: { width: "100px" } }
      ]
    };
  },
  created() {
    this.debouncedSearch = debounce(this.applyFilters, 300);
    this.fetchOffers();
  },
  methods: {
    applyFilters() {
      this.$nextTick(() => {
        this.currentPage = 1;
        this.fetchOffers();
      });
    },
    fetchOffers() {
      this.isTableBusy = true;
      this.$http
        .get(`/enroll_admin/offers`, {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            "filters[search]": this.filters.search || undefined,
            "filters[hide_agency]": this.filters.hide_agency || undefined,
            "filters[hide_feed]": this.filters.hide_feed || undefined,
            "filters[hide_jobbank]": this.filters.hide_jobbank || undefined
          }
        })
        .then(({ data }) => {
          this.currentPage = data.current_page;
          this.total = data.total;
          this.offers = data.data;
        })
        .finally(() => {
          this.isTableBusy = false;
        });
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.card-category {
  color: #9a9a9a;
  font-size: 14px;
}

.font-weight-medium {
  font-weight: 500;
}
</style>
